import { useUnit } from 'effector-react';
import { $additionalProducts } from 'models/additional-products';
import $$ep from 'models/ep';
import navigate from 'models/router-gate';

import DashboardService from 'services/DashboardService';

import useResources from 'hooks/useResources';

import estatePlantIcon from 'assets/icons/estate-plan-icon.svg';
import giftTrustIcon from 'assets/icons/gift-trust.svg';

import { type AdditionalProductNames } from 'types/zod/additional-products';

const iconsByProductName: Record<AdditionalProductNames, string> = {
    giftTrust: giftTrustIcon,
};

const $availableProducts = $additionalProducts.map(
    (products) => keys(products).filter((product) => products[product]) as AdditionalProductNames[],
);

function keys<T extends string | number | symbol>(o: Record<T, unknown>) {
    return Object.keys(o) as (keyof typeof o)[];
}

export default function AdditionalProducts() {
    const { t } = useResources();

    const additionalProducts = useUnit($availableProducts);

    const epExists = useUnit($$ep.stores.$epQInstanceId.map(Boolean));

    const anyAdditionalProducts = additionalProducts.length > 0;

    if (!anyAdditionalProducts && epExists) {
        return null;
    }

    return (
        <>
            <h4 className="serif text-dark">
                {epExists
                    ? t('newDashboardPage.completePage.additionalProducts.title')
                    : t('newDashboardPage.menuItemInitial')}
            </h4>
            <p>{t('newDashboardPage.completePage.additionalProducts.description')}</p>
            <div className="d-grid gap-2 grid-cols-1 md:grid-cols-6">
                {!epExists && (
                    <div className="col-span-3 card p-4 rounded-xl pointer bg-old-primary d-flex justify-content-between flex-column">
                        <div className="card-icon">
                            <img src={estatePlantIcon} alt="Estate Plan" />
                        </div>
                        <h4 className="serif mt-2 text-white">
                            {t('newDashboardPage.completePage.additionalProducts.estatePlan.title')}
                        </h4>
                        <button
                            className="btn bg-primary text-white w-fit"
                            onClick={() => {
                                DashboardService.startAssessment(false);
                            }}
                        >
                            {t('newDashboardPage.completePage.additionalProducts.estatePlan.cta')}
                        </button>
                    </div>
                )}
                {additionalProducts.map((productName) => {
                    const item = t(`newDashboardPage.completePage.additionalProducts.${productName}`, {
                        returnObjects: true,
                    });
                    return (
                        <div
                            key={productName}
                            className="col-span-2 card p-4 rounded-xl pointer"
                            onClick={() => {
                                if (item.linkTo) {
                                    navigate(item.linkTo);
                                }
                            }}
                        >
                            <div className="card-icon">
                                <img src={iconsByProductName[productName]} alt={productName} />
                            </div>
                            <h4 className="mt-2 serif text-old-primary">{item.title}</h4>
                            <span className="mt-1">{item.description}</span>
                            <u>
                                <strong>{item.link}</strong>
                            </u>
                        </div>
                    );
                })}
            </div>
        </>
    );
}
